import React from 'react';

// helpers
import {useTranslation} from 'react-i18next';
import {useQueryClient} from '@tanstack/react-query';
import {checkHasFeatureAccess} from '../helpers';

// services
import {logout} from '../services/auth';
import {UserConfigManager} from '../services/UserConfigManager';
import {changeLanguage} from '../translations';

// icons
import {Icons} from '../components/Menu/Menu-styles';

// types
export type MenuItem = {
  text: string;
  icon?: JSX.Element; // Make icon optional by adding '?'
  rootPath?: string; // used for page items
  action?: () => void; // used for non-page items
  children?: MenuItem[]; // used for sub-menu items
};

const useMenuItems = () => {
  const {t, i18n} = useTranslation();
  const queryClient = useQueryClient();
  const userConfig = UserConfigManager.getConfig();

  /**
   * Handle language change for app
   */
  const handleLanguageChange = async () => {
    await changeLanguage(i18n.language === 'en' ? 'fr' : 'en', queryClient);
  };

  /**
   * Gets the menu items based on the product configuration
   * @returns
   */
  const getMenuItems = () => {
    const items: MenuItem[] = [
      {
        text: t('menu_item_dashboard'),
        icon: React.createElement(Icons.Dashboard),
        rootPath: '/dashboard',
      },
    ];

    if (checkHasFeatureAccess(userConfig, 'subscriptions')) {
      items.push({
        text: t('menu_item_subscriptions'),
        icon: React.createElement(Icons.Subscription),
        rootPath: '/subscriptions',
      });
    }

    if (checkHasFeatureAccess(userConfig, 'loans')) {
      items.push({
        text: t('menu_item_loans'),
        icon: React.createElement(Icons.Loan),
        rootPath: '/loans',
      });
    }

    if (checkHasFeatureAccess(userConfig, 'contentLibrary')) {
      items.push({
        text: t('menu_item_contentLibrary'),
        icon: React.createElement(Icons.Library),
        rootPath: '/articles',
        children: [
          {
            text: t('menu_item_contentLibrary_blog'),
            // icon: React.createElement(Icons.Library),
            rootPath: '/articles/blog',
          },
          {
            text: t('menu_item_contentLibrary_video'),
            // icon: React.createElement(Icons.Library),
            rootPath: '/articles/video',
          },
        ]
      });
    }
    return items;
  };

  /**
   * Gets configuration menu items
   * @returns
   */
  const getConfigMenuItems = () => {
    const configMenuItem: MenuItem[] = [
      {
        text: t('profile'),
        icon: React.createElement(Icons.Profile),
        rootPath: '/profile',
      },
      {
        text: t('opposite_language'),
        icon: React.createElement(Icons.Language),
        action: () => {
          handleLanguageChange();
        },
      },
      {
        text: t('contact_us'),
        icon: React.createElement(Icons.Contact),
        rootPath: '/contact',
      },
      {
        text: t('logout'),
        icon: React.createElement(Icons.Logout),
        action: async () => {
          await logout();
        },
      },
    ];

    return configMenuItem;
  };

  return {
    menuItems: getMenuItems(),
    configMenuItems: getConfigMenuItems(),
  };
};

export default useMenuItems;
